import { FocusEvent, KeyboardEvent, useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { AppLauncherIcon } from '@/components/base/Icons/AppLauncherIcon';
import { useAuthContext } from '@/context/AuthContext';
import { useCentralizedLinkClick } from '@/hooks/AdobeAnalyticsHooks/linkClick/useCentralizedLinkClick';
import Button from '@/storybook/components/Button/Button';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Popover from '@/storybook/components/Popover/Popover';
import { useHoverPopover } from '@/storybook/components/SidebarMenu/useHoverPopover';
import CesNavigationIcon from '@/storybook/icons/CesNavigationIcon/CesNavigationIcon';
import styles from './AppLauncher.module.css';
import AppLink from './AppLink';
import appLinkConfig from './appLinkConfig';

const TRIGGER_TEST_ID = 'ces-top-bar-btn-applauncher';

interface AppLauncherProps {
  onOpen?: () => void;
  sidebar?: boolean;
  expanded?: boolean;
}

export default function AppLauncher({ onOpen, sidebar = false, expanded = false }: AppLauncherProps) {
  const { isOpen, handleOpen, handleClose, handleOpenChange } = useHoverPopover();
  const { userInfo } = useAuthContext();
  const country = userInfo?.countries;
  const isAdmin = userInfo?.isAdmin;
  const { t } = useTranslation();
  const { trackTabClick } = useCentralizedLinkClick();
  const label = t('header.navigation.appLauncher');

  const filteredLinks = useMemo(
    () =>
      appLinkConfig.filter(appLink => {
        const conditions = [
          isAdmin,
          !appLink.allowedCountries?.length,
          country?.length && appLink.allowedCountries?.includes(country[0]),
        ];

        return conditions.some(condition => condition);
      }),
    [isAdmin, country],
  );

  const handleAppLauncherClick = () => {
    trackTabClick({
      innertext: label,
      position: 'APPLICATION_HEADER',
      name: 'APP_LAUNCHER_TAB',
    });
  };

  const handleSidebarTriggerClick = () => {
    handleAppLauncherClick();
    handleOpen();
  };

  const handleSidebarKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') handleClose();
  };

  const handleSidebarBlur = (event: FocusEvent) => {
    if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) handleClose();
  };

  const sidebarIcon = <CesNavigationIcon name="AppLauncher" />;
  const sidebarTrigger = expanded ? (
    <Button
      variant="text"
      size="S"
      startIcon={sidebarIcon}
      className={styles.sidebarTrigger}
      aria-expanded={isOpen}
      data-testid={TRIGGER_TEST_ID}
      onClick={handleSidebarTriggerClick}
    >
      {label}
    </Button>
  ) : (
    <IconButton
      appearance="text"
      size="S"
      icon={sidebarIcon}
      className={styles.sidebarTrigger}
      aria-label={label}
      aria-expanded={isOpen}
      data-testid={TRIGGER_TEST_ID}
      onClick={handleSidebarTriggerClick}
    />
  );
  const headerTrigger = (
    <IconButton
      appearance="text"
      size="M"
      icon={<AppLauncherIcon />}
      className={styles.appLauncherIconBg}
      aria-label={label}
      data-testid={TRIGGER_TEST_ID}
      onClick={handleAppLauncherClick}
    />
  );

  const popover = (
    <Popover
      onActiveFn={onOpen}
      className={styles.appLauncherContainer}
      title={sidebar ? undefined : label}
      raw={sidebar}
      trigger={sidebar ? 'hover' : 'click'}
      isOpen={sidebar ? isOpen : undefined}
      onOpenChange={sidebar ? handleOpenChange : undefined}
      size="S"
      noticePosition={sidebar ? 'right' : 'bottom'}
      disableAutoPosition={!sidebar}
      portal={false}
      appearance="primary"
      overlayingNoticeTestId="popup"
      anchorContent={sidebar ? sidebarTrigger : headerTrigger}
      noticeContent={
        <div className={cx(sidebar && styles.sidebarPanel)}>
          {sidebar && <div className={styles.sidebarTitle}>{t('header.sidebar.otherApplications')}</div>}
          <div className={cx(styles.popupContainerInner, sidebar && styles.sidebarGrid)}>
            {filteredLinks.map(link => (
              <AppLink key={link.url} url={link.url} Icon={link.Icon} name={link.name} />
            ))}
          </div>
        </div>
      }
    />
  );

  if (!sidebar) return popover;
  return (
    <div onKeyDown={handleSidebarKeyDown} onBlur={handleSidebarBlur}>
      {popover}
    </div>
  );
}
